import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "api/apiClient.ts";

interface Order {
  _id: string;
  products: Product[];
  total: number;
  paymentMethod: string;
  status: string;
  date: string;
  mosque: {
    _id: string;
    name: string;
  };
  customer: {
    _id: string;
    name: string;
  };
  assignedTo?: string;
}

interface Product {
  _id: string;
  product: {
    _id: string;
    name: {
      ar: string;
      en: string;
    };
    category: {
      _id: string;
      name: {
        ar: string;
        en: string;
      };
    };
  };
  quantity: number;
}
const fetchOrders = async (): Promise<Order[]> => {
  const allOrders: Order[] = [];
  let page = 1;
  let hasMoreData = true;

  while (hasMoreData) {
    const { data } = await apiClient.get(`/orders?limit=50&page=${page}`);

    if (data.status !== "success" || !Array.isArray(data.data.orders)) {
      throw new Error("Invalid response format");
    }

    const orders = data.data.orders;
    const processedOrders = orders.map((order) => ({
      ...order,
      products: order.products.map((p) => ({
        ...p,
        product: p.product || { name: { en: "Unknown", ar: "غير معروف" } },
      })),
    }));

    allOrders.push(...processedOrders);

    // If we get less than 50 orders, we've reached the last page
    if (orders.length < 50) {
      hasMoreData = false;
    } else {
      page++;
    }
  }

  return allOrders;
};
export const useOrders = () => {
  return useQuery({
    queryKey: ["orders"],
    queryFn: fetchOrders,
  });
};
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, orderStatus }: { id: string; orderStatus: string }) => {
      const response = await apiClient.patch(`/orders/update/${id}`, { orderStatus });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const useAssignOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, userId }: { orderId: string; userId: string }) => {
      const apiUrl = `/orders/update/${orderId}`;

      try {
        const response = await apiClient.patch(apiUrl, { operator: userId });
        return response.data;
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};
